/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { LuisConstants } from 'src/core/utils/LuisConstants';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerContextTreeNode } from 'src/qnaMaker/core/types/QnaMakerContextTreeNode';

export const getQnaIdToTreeNodesMap = (qnaPairsJson: QnaMakerQnaSwagger[]): Map<number, QnaMakerContextTreeNode[]> => {
    const qnaIdToQnaPairMap = new Map<number, QnaMakerQnaSwagger>();
    const qnaIdToParentsMap = new Map<number, QnaMakerContextTreeNode[]>();

    qnaPairsJson.forEach(pair => qnaIdToQnaPairMap.set(pair.id, pair));

    // Turn the pair to tree node having all its children inside recursively.
    const pairToTreeNode = (
        parentId: number,
        pair: QnaMakerQnaSwagger,
        level: number,
        displayText?: string,
        ancestorIds: number[] = []
    ): QnaMakerContextTreeNode => {
        if (!pair || level >= LuisConstants.QnaMaker.ContextTreeMaxDepth) {
            return;
        }

        const detectedCyclicDep = ancestorIds.includes(pair.id);

        return {
            id: `${parentId}-${pair.id}`,
            qnaId: pair.id,
            displayText: displayText ?? pair.questions[0],
            children:
                !detectedCyclicDep && level < LuisConstants.QnaMaker.ContextTreeMaxDepth
                    ? pair.dialog.prompts
                          .map(p =>
                              pairToTreeNode(pair.id, qnaIdToQnaPairMap.get(p.qnaId), level + 1, p.displayText, [...ancestorIds, parentId])
                          )
                          .filter(Boolean)
                    : [],
            level
        };
    };

    // Constructing the tree roots for each pair
    qnaPairsJson.forEach(pair => {
        const parentIdsLength = pair.parentQnaIds?.length ?? 0;
        const haveChildren = pair.dialog.prompts.length;
        const treeRoots: QnaMakerContextTreeNode[] =
            pair.parentQnaIds
                ?.map((parentId, index) => {
                    // Only the last tree per qna pair will have the whole completed tree and
                    // the others will only contain parent / pair nodes.
                    const lastParentId = index === parentIdsLength - 1;
                    const canConstructTree = lastParentId && haveChildren;
                    const parentPair = qnaIdToQnaPairMap.get(parentId);

                    if (!canConstructTree && !parentPair?.dialog.prompts.find(p => p.qnaId === pair.id)) {
                        return;
                    }

                    return {
                        id: `${parentId}-${pair.id}`,
                        qnaId: parentId,
                        displayText: parentPair.questions[0],
                        children: [
                            canConstructTree
                                ? pairToTreeNode(pair.id, pair, 0)
                                : {
                                      id: `${pair.id}`,
                                      qnaId: pair.id,
                                      displayText: parentPair.dialog.prompts.find(p => p.qnaId === pair.id).displayText,
                                      children: [],
                                      level: 0,
                                      height: 0
                                  }
                        ],
                        level: -1
                    };
                })
                .filter(Boolean) ?? [];

        // If there are no parents so the only tree will be constructed from the qna pair itself as root.
        if (!pair.parentQnaIds?.length || !treeRoots.length) {
            treeRoots.push(pairToTreeNode(pair.id, pair, 0));
        }

        qnaIdToParentsMap.set(pair.id, treeRoots);
    });

    return qnaIdToParentsMap;
};
